<script setup>
import { onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import mushroom from "cem-probe-api";
import Loading from "@/components/loading-hello.vue";
import { getSystemType } from "@/utils/systemType";

const route = useRoute();
const router = useRouter();

const decodeQueryParam = (val) => {
  if (!val) return "";
  try {
    return decodeURIComponent(val);
  } catch (e) {
    return val;
  }
};

onMounted(async () => {
  const query = route.query;
  const fullPath = route.fullPath;

  if (fullPath.includes("backToProbe")) {
    const url = decodeQueryParam(query.url);
    const token = decodeQueryParam(query.token);

    if (token) {
      await mushroom.$auth.setTokenAsync(token, true);
    }
    if (url) {
      window.location.href = url;
    } else {
      router.push("/home");
    }
  } else {
    const tokenFromPrimary = decodeQueryParam(query.access_token);
    const pidProbe = query.pid_probe || query.pid || ""; // backward compat với primary cũ
    const pidSummary = query.pid_summary || "";
    const systemType = query.system_type || "";
    const menu = query.menu;
    const dynamicDashboardId = query.dynamic_dashboard_id || "";
    const language = query.lang || "vi";
    const summaryName = query.summary_name
      ? decodeQueryParam(query.summary_name)
      : "";

    // Kiểm tra xem pid_probe mới có khác pid_probe cũ trong localStorage không
    const incomingPidProbe = query.pid_probe || "";
    const existingPidProbe = window.localStorage.getItem("pid_probe") || "";

    // Nếu đổi trạm (khác pid_probe), xóa urlOld để điều hướng về /home của trạm mới
    if (incomingPidProbe && existingPidProbe && incomingPidProbe !== existingPidProbe) {
      window.localStorage.removeItem("urlOld");
    }

    // Lưu toàn bộ các url_* từ query vào localStorage
    Object.keys(query).forEach((key) => {
      if (key.toLowerCase().startsWith("url")) {
        const value = decodeQueryParam(query[key]);
        // Chỉ lưu URL hợp lệ (bắt đầu bằng http/https)
        if (!value || !/^https?:\/\//i.test(value)) return;
        const formattedValue = value.endsWith("/") ? value : value + "/";
        window.localStorage.setItem(key, formattedValue);
      }
    });

    // Lưu system_type từ target_domain của menu
    if (systemType) {
      window.localStorage.setItem("system_type", systemType);
    }

    // Lưu type_login từ primary (dùng để trả lại primary khi quay về)
    const typeLogin = query.type_login || "";
    if (typeLogin) {
      window.localStorage.setItem("type_login", typeLogin);
    }

    // Luôn lưu pid_probe và pid_summary (kể cả rỗng) để đảm bảo đồng bộ
    window.localStorage.setItem("pid_probe", pidProbe);
    window.localStorage.setItem("pid_summary", pidSummary);
    if (tokenFromPrimary) {
      await mushroom.$auth.setTokenAsync(tokenFromPrimary, true);
    }
    window.localStorage.setItem("language", language);
    if (summaryName) {
      window.localStorage.setItem("summaryName", summaryName);
    } else {
      window.localStorage.removeItem("summaryName");
    }

    const excludeKeys = [
      "access_token",
      "urlPrimary",
      "pid",
      "nameProbe",
      "type_login",
      "menu",
      "dynamic_dashboard_id",
      "url_probe",
      "url_summary",
      "lang",
      "system_type",
      "summary_name",
      "pid_probe",
      "pid_summary",
    ];
    const nextQuery = {};
    Object.keys(query).forEach((key) => {
      if (!excludeKeys.includes(key) && !key.toLowerCase().startsWith("url")) {
        nextQuery[key] = query[key];
      }
    });

    let path = "/home";
    if (menu) {
      path = menu.startsWith("/") ? menu : `/${menu}`;
    } else if (dynamicDashboardId) {
      path = `/dashboard/${dynamicDashboardId}`;
    } else {
      try {
        const userResponse = await mushroom.$auth.meAsync();
        if (userResponse) {
          window.localStorage.setItem("userInfo", JSON.stringify(userResponse));
        }

        const currentSystemType = getSystemType();
        const menuRes = await mushroom.menu.views.get_treeAsync(
          {
            domains: ["primary", currentSystemType],
            currentDomain: currentSystemType,
          },
          { clientCache: true },
        );

        if (menuRes.result && Array.isArray(menuRes.result)) {
          // Bước 1: Trích xuất tất cả các liên kết (links) nội bộ Probe mà user được phép truy cập
          const allowedLinks = [];
          const extractLinks = (items, isMenuGoc = false) => {
            items.forEach((item) => {
              // Kiểm tra xem menu có thuộc phân hệ hiện tại hay không (không phải cross-domain sang primary/summary)
              const isLocal =
                !item.target_domain || item.target_domain === currentSystemType;
              // Bỏ qua menu cấp 1 mà có con (vì cấp 1 có con không sử dụng link trên giao diện menu)
              const isMenuCha = isMenuGoc && item.children && item.children.length > 0;
              // Nếu thuộc phân hệ hiện tại và có link hợp lệ (không phải '#' hoặc rỗng) thì thêm vào danh sách
              if (isLocal && item.link && item.link !== "#" && !isMenuCha) {
                allowedLinks.push(item.link);
              }
              // Nếu menu cha có menu con thì tiếp tục đệ quy xuống cấp dưới để lấy link
              if (item.children) {
                extractLinks(item.children, false);
              }
            });
          };
          extractLinks(menuRes.result, true); // gán các link thỏa mãn vào trong allowedLinks

          // Bước 2: Kiểm tra xem đường dẫn đích hiện tại (path) có nằm trong danh sách được phép hay không
          //  bỏ các tham số query, hash trước khi so sánh
          const normTarget = path.split("?")[0].split("#")[0];
          const isPathAllowed = allowedLinks.some((link) => {
            const normLink = link.split("?")[0].split("#")[0];
            return (
              normTarget === normLink || normTarget.startsWith(normLink + "/")
            );
          });
          console.log('allowedLinks', allowedLinks)
          // Bước 3: Nếu không có quyền truy cập vào đường dẫn đích, tự động chuyển hướng sang menu hợp lệ đầu tiên
          if (!isPathAllowed) {
            const hasHome = allowedLinks.some((link) => {
              const normLink = link.split("?")[0].split("#")[0];
              return normLink === "/home";
            });
            if (hasHome) {
              path = "/home";
            } else if (allowedLinks.length > 0) {
              path = allowedLinks[0];
            } else {
              path = "/no-menu";
              console.warn(
                "Không tìm thấy liên kết menu hợp lệ nào cho người dùng hiện tại.",
              );
            }
          }
        }
      } catch (e) {
        console.error("Lỗi xử lý phân quyền dynamic routing:", e);
      }
    }
    console.log({ path: path, nextQuery: nextQuery });
    const target = router.resolve(path);
    router.replace({
      path: target.path,
      query: { ...target.query, ...nextQuery },
      hash: target.hash || undefined,
    });
  }
});
</script>

<template>
  <Loading />
</template>
